import { useEffect, useState } from "react";
import type { SocialProvider } from "../api/account";

/*
 * Google and Facebook sign-in in the browser (design doc §6.1). Each gives an access token,
 * which the server checks with the provider before believing it. The scripts load when the
 * page opens: a popup is only allowed straight from a click, so nothing may be awaited
 * between the click and asking for the token.
 */

type GoogleTokenResponse = { access_token?: string; error?: string };
type GoogleTokenClient = { requestAccessToken: () => void };
type FacebookLoginResponse = { authResponse?: { accessToken: string } | null };

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: GoogleTokenResponse) => void;
            error_callback?: (error: { type: string }) => void;
          }) => GoogleTokenClient;
        };
      };
    };
    FB?: {
      init: (config: { appId: string; version: string; cookie: boolean; xfbml: boolean }) => void;
      login: (callback: (response: FacebookLoginResponse) => void, options: { scope: string }) => void;
    };
  }
}

const GOOGLE_SCRIPT = "https://accounts.google.com/gsi/client";
const FACEBOOK_SCRIPT = "https://connect.facebook.net/en_US/sdk.js";
// The Graph API version the Facebook app is set to. Check it matches the app's dashboard.
const FACEBOOK_API_VERSION = "v26.0";

const loading = new Map<string, Promise<void>>();

function loadScript(src: string): Promise<void> {
  let promise = loading.get(src);
  if (!promise) {
    promise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        loading.delete(src);
        reject(new Error(`Couldn't load ${src}`));
      };
      document.head.append(script);
    });
    loading.set(src, promise);
  }
  return promise;
}

export type SocialIds = { google_client_id: string | null; facebook_app_id: string | null };

/**
 * Loads the scripts for the providers this server has set up. `ready` says which can be
 * clicked; `token` opens the provider's popup and resolves with an access token, or null
 * if the customer closed it.
 */
export function useSocialSignIn(ids: SocialIds | undefined) {
  const [ready, setReady] = useState<Record<SocialProvider, boolean>>({ google: false, facebook: false });
  const googleId = ids?.google_client_id;
  const facebookId = ids?.facebook_app_id;

  useEffect(() => {
    if (googleId) loadScript(GOOGLE_SCRIPT).then(() => setReady((now) => ({ ...now, google: true })), () => undefined);
    if (facebookId) {
      loadScript(FACEBOOK_SCRIPT).then(() => {
        window.FB?.init({ appId: facebookId, version: FACEBOOK_API_VERSION, cookie: false, xfbml: false });
        setReady((now) => ({ ...now, facebook: true }));
      }, () => undefined);
    }
  }, [googleId, facebookId]);

  const token = (provider: SocialProvider): Promise<string | null> =>
    new Promise((resolve, reject) => {
      if (provider === "google" && googleId && window.google) {
        window.google.accounts.oauth2
          .initTokenClient({
            client_id: googleId,
            scope: "openid email profile",
            callback: (response) => (response.access_token ? resolve(response.access_token) : reject(new Error(response.error ?? "google"))),
            error_callback: (error) => (error.type === "popup_closed" ? resolve(null) : reject(new Error(error.type))),
          })
          .requestAccessToken();
      } else if (provider === "facebook" && window.FB) {
        window.FB.login((response) => resolve(response.authResponse?.accessToken ?? null), { scope: "email" });
      } else {
        reject(new Error(`${provider} isn't ready`));
      }
    });

  return { available: { google: Boolean(googleId), facebook: Boolean(facebookId) }, ready, token };
}
